import React, {useState, useEffect} from 'react';
import axios from 'axios';
import {useUser} from '@/contexts/UserContext';
import {enqueueSnackbar} from 'notistack';
import {KeenIcon} from "@/components";
import {Loading} from "@/layouts/buddy/loading";
import {ScheduledDate, WebinarRegisterResponse} from "@/pages/buddy/types/Webinar.ts";

const Webinar: React.FC = () => {
    const {user} = useUser();
    const [dates, setDates] = useState<ScheduledDate[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [signingUp, setSigningUp] = useState<Record<number, boolean>>({});
    const [registered, setRegistered] = useState<ScheduledDate | null>(null);

    const API_URL = import.meta.env.VITE_APP_API_URL as string;

    useEffect(() => {
        const fetchDates = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const res = await axios.get<ScheduledDate[]>(
                    `${API_URL}/webinar/next`,
                    {headers: {Authorization: `Bearer ${token}`}}
                );
                setDates(res.data);
            } catch {
                enqueueSnackbar('Failed to load webinar dates.', {variant: 'error'});
            } finally {
                setLoading(false);
            }
        };
        fetchDates();
    }, [API_URL]);

    const handleSignup = async (dateId: number) => {
        setSigningUp(prev => ({...prev, [dateId]: true}));
        try {
            const token = localStorage.getItem('authToken');
            const res = await axios.put<WebinarRegisterResponse>(
                `${API_URL}/webinar/register`,
                null,
                {
                    params: {date_id: dateId},
                    headers: {Authorization: `Bearer ${token}`},
                }
            );

            const registeredDate = dates.find(d => d.date_id === dateId);
            if (registeredDate) setRegistered(registeredDate);

            enqueueSnackbar('Successfully registered!', {variant: 'success'});
        } catch {
            enqueueSnackbar('Registration failed. Please try again.', {variant: 'error'});
        } finally {
            setSigningUp(prev => ({...prev, [dateId]: false}));
        }
    };

    if (loading) return <Loading loading={loading}/>;

    return (
        <div className="card">
            <div className="card-body px-10 py-7.5 lg:pr-12.5">
                <div className="flex flex-wrap md:flex-nowrap items-center gap-6 md:gap-10">
                    <div className="flex flex-col gap-3">
                        {registered ? (
                            <>
                                <h1 className="text-2xl font-semibold text-gray-900 mb-1">
                                    You're registered!
                                </h1>
                                <h2 className="text-1xl font-semibold text-gray-900">
                                    You’ve successfully signed up for the group onboarding on:
                                </h2>

                                <h2 className="text-md text-gray-900 mb-1">
                                    {formatDateWithWeekday(registered.datetime)} ({registered.zone})

                                </h2>
                                <p className="text-sm text-gray-700 leading-5.5">
                                    A confirmation email has been sent. You’ll also receive reminders before the event.
                                </p>
                            </>
                        ) : (
                            <>
                                <h1 className="text-2xl font-semibold text-gray-900">
                                    Join our free onboarding webinar
                                </h1>
                                <h2 className="text-1xl font-semibold text-gray-900">
                                    Meet the founder and our HR experts - pick a time that suits you
                                </h2>

                                <p className="text-sm text-gray-700 leading-5.5">
                                    These 30-minute sessions are perfect for anyone looking to get started with Buddy.
                                    Discover how to make the most of the platform, ask your questions live, and explore
                                    how AI can enhance your HR and recruitment processes. Select a time slot below and
                                    sign up in just one click – we’ll send the link straight to your inbox.
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                                    {dates.map(d => {
                                        const formatted = formatDateWithWeekday(d.datetime);
                                        return (
                                            <div key={d.date_id} className="card">
                                                <div className="card-header flex justify-between items-center">
                                                    <h3 className="card-title">Group Onboarding Session</h3>
                                                </div>

                                                <div className="card-body flex flex-col justify-between">
                                                    <div>
                                                        <h2 className="text-md text-gray-900 mb-1">{formatted} ({d.zone})</h2>
                                                        <p className="text-sm text-gray-700 mb-4">Includes live Q&A</p>
                                                    </div>

                                                    <button
                                                        onClick={() => handleSignup(d.date_id)}
                                                        disabled={signingUp[d.date_id]}
                                                        className="btn btn-primary btn-sm flex items-center gap-2 justify-center"
                                                    >
                                                        <KeenIcon icon="abstract-6"/>
                                                        {signingUp[d.date_id] ? 'Registering…' : 'Sign up now'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Webinar;

const formatDateWithWeekday = (datetime: string): string => {
    const date = new Date(datetime);
    if (isNaN(date.getTime())) return datetime;

    const weekday = date.toLocaleDateString('en-US', {weekday: 'long'});
    return `${weekday}, ${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}, ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}h`;
};

interface AddToCalendarProps {
    datetime: string;
    title: string;
}


